import type { ClientMessage, ServerMessage } from '@simpleacs/shared';

type Listener = (message: ServerMessage) => void;

export type ConnectionState = 'connecting' | 'open' | 'closed';

/**
 * One WebSocket per tab, shared by every hook that needs live data.
 *
 * Subscriptions are re-sent after a reconnect, so a dropped socket recovers
 * without the pages that use it having to know it happened.
 */
class RealtimeConnection {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private stateListeners = new Set<(state: ConnectionState) => void>();
  private topics = new Set<string>();
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private heartbeat: number | null = null;
  private state: ConnectionState = 'closed';

  getState(): ConnectionState {
    return this.state;
  }

  onMessage(listener: Listener): () => void {
    this.listeners.add(listener);
    this.ensureConnected();
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) this.close();
    };
  }

  onState(listener: (state: ConnectionState) => void): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => this.stateListeners.delete(listener);
  }

  subscribe(topics: string[]): void {
    for (const topic of topics) this.topics.add(topic);
    this.ensureConnected();
    this.send({ type: 'subscribe', topics });
  }

  unsubscribe(topics: string[]): void {
    for (const topic of topics) this.topics.delete(topic);
    this.send({ type: 'unsubscribe', topics });
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    for (const listener of this.stateListeners) listener(state);
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  private ensureConnected(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.setState('connecting');
    const socket = new WebSocket(`${protocol}//${location.host}/ws`);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.reconnectAttempts = 0;
      this.setState('open');
      if (this.topics.size > 0) this.send({ type: 'subscribe', topics: [...this.topics] });
      this.heartbeat = window.setInterval(() => this.send({ type: 'ping' }), 25_000);
    });

    socket.addEventListener('message', (event) => {
      let parsed: ServerMessage;
      try {
        parsed = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;
      }
      for (const listener of this.listeners) listener(parsed);
    });

    socket.addEventListener('close', () => {
      this.setState('closed');
      if (this.heartbeat) window.clearInterval(this.heartbeat);
      this.heartbeat = null;
      this.socket = null;
      if (this.listeners.size > 0) this.scheduleReconnect();
    });

    socket.addEventListener('error', () => socket.close());
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    // 1s, 2s, 4s … capped at 15s, so a restarting API is picked up quickly
    // without hammering it.
    const delay = Math.min(15_000, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.ensureConnected();
    }, delay);
  }

  private close(): void {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.heartbeat) window.clearInterval(this.heartbeat);
    this.heartbeat = null;
    this.topics.clear();
    this.socket?.close();
    this.socket = null;
    this.setState('closed');
  }
}

export const realtime = new RealtimeConnection();
